import React from 'react';
import { MdBadge, MdMail, MdPerson } from 'react-icons/md';
import styled, { css } from 'styled-components/macro';
import { HiOfficeBuilding } from 'react-icons/hi';
import { useMainProviderContext } from 'context/main/provider';
import { Gap } from 'components/layout';
import { IoMdPhonePortrait } from 'react-icons/io';
import { BsGlobe } from 'react-icons/bs';
import { StyledLink } from './styles';
import {
  FONT_CASE_KEYWORD,
  FONT_WEIGHT_KEYWORD,
} from 'queries/details/usePlaybackDetailsQuery';
import { sanitizePhoneForTel } from '../../utils/helpers';

const HeaderWrapper = styled.div<{
  backgroundColor: string;
  color: string;
  aligment: string;
  showHeaderShadow: boolean;
}>`
  background-color: ${({ backgroundColor }) => backgroundColor || '#fff'};
  color: ${({ color }) => color || '#fff'};
  min-height: 120px;
  box-sizing: border-box;

  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px;
  width: 100%;
  flex-wrap: wrap;
  gap: 32px;
  flex-grow: 0;

  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 6px;
    pointer-events: none;
    box-shadow: ${({ showHeaderShadow }) =>
      showHeaderShadow ? '0 2px 4px -1px rgba(0, 0, 0, 0.12)' : 'none'};
    background: transparent;
  }
`;

const ContactWrapper = styled.div<{
  orientation: string;
  aligment: string;
  showIcon: boolean;
  headerFontSize: number;
  headerFontCase: FONT_CASE_KEYWORD;
  headerFontWeight: FONT_WEIGHT_KEYWORD;
  headerLineHeight: number;
}>`
  display: flex;

  font-size: ${({ headerFontSize }) =>
    headerFontSize ? headerFontSize + 'px' : '12px'};
  line-height: ${({ headerLineHeight }) => `${headerLineHeight}px`};
  text-transform: ${({ headerFontCase }) => headerFontCase};
  font-weight: ${({ headerFontWeight }) => parseInt(headerFontWeight)};

  ${({ orientation }) =>
    orientation === 'vertical' &&
    css`
      flex-direction: column;
      gap: 8px;
    `}
  ${({ orientation }) =>
    orientation === 'horizontal' &&
    css`
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 16px;
    `}
    ${({ showIcon }) => css`
    svg {
      display: ${showIcon ? 'block' : 'none'};
    }
  `}

  @media screen and (max-width: 520px) {
    justify-content: center;
    flex-direction: column;
    width: 100%;
    gap: 16px;
  }
`;

const LogoWrapper = styled.div`
  flex: 1;
  display: flex;
  @media screen and (max-width: 520px) {
    align-items: center;
    justify-content: center;
  }
`;

export const LandingHeader = () => {
  const { response } = useMainProviderContext();

  const { landingPageDesign, advisor } = response;
  const { titleFontSize, headerFontCase, headerFontWeight, headerLineHeight } =
    landingPageDesign;

  const showFirstName =
    !!landingPageDesign.showFirstName && !!advisor.firstName;
  const showLastName = !!landingPageDesign.showLastName && !!advisor.lastName;
  const showPhone1 = !!landingPageDesign.showPhone1 && !!advisor.phone;
  const showPhone2 = !!landingPageDesign.showPhone2 && !!advisor.phone2;
  const showEmail = !!landingPageDesign.showEmail && !!advisor.email;
  const showHeaderShadow = !!landingPageDesign.showHeaderShadow;
  const showDepartment =
    !!landingPageDesign.showDepartment && !!advisor.department;
  const showTitle = !!landingPageDesign.showTitle && !!advisor.title;
  const showWebsite = !!landingPageDesign.showWebsite && !!advisor.website;
  const showContactIcons = !!landingPageDesign.showContactIcons;

  const websiteLink = advisor?.website?.includes('https')
    ? advisor?.website
    : `https://${advisor?.website}`;

  const headerLogo = landingPageDesign?.headerLogo || response?.customerLogo;

  return (
    <HeaderWrapper
      backgroundColor={landingPageDesign.headerBackgroundColor}
      color={landingPageDesign.headerTextColor}
      aligment={landingPageDesign.headerContentAlignment}
      showHeaderShadow={showHeaderShadow}
    >
      <LogoWrapper>
        {headerLogo && (
          <img className='header-logo-img' src={headerLogo} alt='header-logo' />
        )}
      </LogoWrapper>

      <ContactWrapper
        className='header-text'
        orientation={landingPageDesign.headerContentOrientation}
        aligment={landingPageDesign.headerContentAlignment}
        showIcon={showContactIcons}
        headerFontSize={titleFontSize}
        headerFontCase={headerFontCase}
        headerFontWeight={headerFontWeight}
        headerLineHeight={headerLineHeight}
      >
        {(showFirstName || showLastName) && (
          <Gap gap='4px' style={{ whiteSpace: 'nowrap' }} flexWrap='nowrap'>
            <MdPerson color='inherit' size={titleFontSize} />
            <div>
              {showFirstName && advisor.firstName}{' '}
              {showLastName && advisor.lastName}
            </div>
          </Gap>
        )}

        {showPhone1 && (
          <StyledLink href={`tel: ${sanitizePhoneForTel(advisor.phone)}`}>
            <Gap gap='4px' style={{ whiteSpace: 'nowrap' }} flexWrap='nowrap'>
              <IoMdPhonePortrait color='inherit' size={titleFontSize} />
              {advisor.phone}
            </Gap>
          </StyledLink>
        )}

        {showPhone2 && (
          <StyledLink href={`tel: ${sanitizePhoneForTel(advisor.phone2)}`}>
            <Gap gap='4px' style={{ whiteSpace: 'nowrap' }} flexWrap='nowrap'>
              <IoMdPhonePortrait color='inherit' size={titleFontSize} />
              {advisor.phone2}
            </Gap>
          </StyledLink>
        )}

        {showEmail && (
          <StyledLink href={`mailto:${advisor.email}`}>
            <Gap gap='4px' style={{ whiteSpace: 'nowrap' }} flexWrap='nowrap'>
              <MdMail color='inherit' size={titleFontSize} />
              {advisor.email}
            </Gap>
          </StyledLink>
        )}

        {showDepartment && (
          <Gap gap='4px' style={{ whiteSpace: 'nowrap' }} flexWrap='nowrap'>
            <HiOfficeBuilding color='inherit' size={titleFontSize} />
            {advisor.department}
          </Gap>
        )}

        {showTitle && (
          <Gap style={{ whiteSpace: 'nowrap' }} gap='4px' flexWrap='nowrap'>
            <MdBadge color='inherit' size={titleFontSize} />
            {advisor.title}
          </Gap>
        )}

        {showWebsite && (
          <StyledLink
            href={websiteLink}
            target='_blank'
            rel='noreferrer noopener'
            style={{ color: 'inherit' }}
          >
            <Gap gap='4px' flexWrap='nowrap'>
              <BsGlobe color='inherit' size={titleFontSize} />
              {advisor.website}
            </Gap>
          </StyledLink>
        )}
      </ContactWrapper>
    </HeaderWrapper>
  );
};
